/**
 * SchemaStack — JSON-LD structured data for all public pages
 * Renders multiple schema types per page for maximum search visibility.
 */
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const SITE = 'https://web-techservices.com';
const JDP = 'https://jobdispatchpro.com';

// ── Core Organization (sitewide) ──
const orgSchema = {
  "@type": "Organization",
  "@id": `${SITE}/#organization`,
  name: "Web-TechServices",
  url: SITE,
  logo: `${SITE}/logo.png`,
  sameAs: [
    "https://www.facebook.com/webtechservices",
    "https://www.linkedin.com/company/web-techservices"
  ],
  contactPoint: {
    "@type": "ContactPoint",
    telephone: "+1-817-320-5334",
    contactType: "sales",
    availableLanguage: ["English", "Spanish"]
  }
};

// ── WebSite (sitewide) ──
const webSiteSchema = {
  "@type": "WebSite",
  "@id": `${SITE}/#website`,
  url: SITE,
  name: "Web-TechServices",
  publisher: { "@id": `${SITE}/#organization` }
};

// ── LocalBusiness (core) ──
const localBusinessSchema = {
  "@type": "ProfessionalService",
  "@id": `${SITE}/#business`,
  name: "Web-TechServices",
  url: SITE,
  telephone: "+1-817-320-5334",
  address: {
    "@type": "PostalAddress",
    streetAddress: "1180 N Town East Blvd Suite 243",
    addressLocality: "Mesquite",
    addressRegion: "TX",
    postalCode: "75150",
    addressCountry: "US"
  },
  geo: {
    "@type": "GeoCoordinates",
    latitude: "32.7767",
    longitude: "-96.5992"
  },
  openingHoursSpecification: {
    "@type": "OpeningHoursSpecification",
    dayOfWeek: ["Monday","Tuesday","Wednesday","Thursday","Friday"],
    opens: "09:00",
    closes: "18:00"
  },
  priceRange: "$$",
  areaServed: { "@type": "Country", name: "United States" }
};

// ── Per-page schemas ──
const PAGE_SCHEMAS = {
  "/": [
    orgSchema,
    webSiteSchema,
    localBusinessSchema,
    {
      "@type": "Service",
      serviceType: "Digital Marketing",
      provider: { "@id": `${SITE}/#organization` },
      areaServed: { "@type": "Country", name: "US" },
      hasOfferCatalog: {
        "@type": "OfferCatalog",
        name: "Marketing Services",
        itemListElement: [
          { "@type": "Offer", itemOffered: { "@type": "Service", name: "Local SEO" } },
          { "@type": "Offer", itemOffered: { "@type": "Service", name: "Website Design" } },
          { "@type": "Offer", itemOffered: { "@type": "Service", name: "Social Media Management" } },
          { "@type": "Offer", itemOffered: { "@type": "Service", name: "PPC Advertising" } },
          { "@type": "Offer", itemOffered: { "@type": "Service", name: "AI Visibility Optimization" } },
        ]
      }
    }
  ],

  "/local-business": [
    orgSchema,
    {
      "@type": "Service",
      name: "Local Business SEO",
      provider: { "@id": `${SITE}/#organization` },
      serviceType: "Local SEO",
      description: "Comprehensive local SEO services for small businesses including Google Business Profile optimization, citation building, and local content strategy."
    }
  ],

  "/seo-turbo": [
    orgSchema,
    {
      "@type": "Product",
      name: "SEO Turbo Package",
      brand: { "@id": `${SITE}/#organization` },
      description: "Accelerated SEO package for rapid search engine ranking improvements.",
      offers: {
        "@type": "AggregateOffer",
        priceCurrency: "USD",
        lowPrice: "299",
        highPrice: "999",
        offerCount: "3"
      }
    }
  ],

  "/about": [
    orgSchema,
    localBusinessSchema,
    {
      "@type": "AboutPage",
      name: "About Web-TechServices",
      description: "Full-service digital marketing agency specializing in local SEO, web design, and AI-powered visibility solutions.",
      mainEntity: { "@id": `${SITE}/#organization` }
    }
  ],

  "/portfolio": [
    orgSchema,
    {
      "@type": "CollectionPage",
      name: "Portfolio — Web-TechServices",
      description: "View our portfolio of web design, SEO, and digital marketing projects."
    }
  ],

  "/faq": [
    orgSchema,
    {
      "@type": "FAQPage",
      mainEntity: [
        { "@type": "Question", name: "What digital marketing services do you offer?", acceptedAnswer: { "@type": "Answer", text: "We offer local SEO, website design, social media management, PPC advertising, AI visibility optimization, and field service management software." } },
        { "@type": "Question", name: "How long does SEO take to show results?", acceptedAnswer: { "@type": "Answer", text: "Most clients see measurable improvements within 3-6 months, with significant results in 6-12 months depending on competition and current standing." } },
        { "@type": "Question", name: "Do you offer free consultations?", acceptedAnswer: { "@type": "Answer", text: "Yes, we offer free initial consultations to understand your business needs and recommend the best strategy." } },
      ]
    }
  ],

  "/review-us": [
    orgSchema,
    localBusinessSchema,
    {
      "@type": "AggregateRating",
      itemReviewed: { "@id": `${SITE}/#business` },
      ratingValue: "4.9",
      bestRating: "5",
      ratingCount: "127"
    }
  ],

  "/ai-search": [
    orgSchema,
    {
      "@type": "SoftwareApplication",
      name: "AI Visibility Checker",
      applicationCategory: "BusinessApplication",
      operatingSystem: "Web",
      offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
      description: "Check how visible your business is across AI search engines like ChatGPT, Perplexity, and Google AI Overviews."
    }
  ],

  "/ppc-audit": [
    orgSchema,
    {
      "@type": "Service",
      name: "PPC Audit",
      provider: { "@id": `${SITE}/#organization` },
      serviceType: "Pay-Per-Click Advertising Audit",
      offers: { "@type": "Offer", price: "0", priceCurrency: "USD", description: "Free PPC audit" }
    }
  ],

  "/ppc-management": [
    orgSchema,
    {
      "@type": "Service",
      name: "PPC Management",
      provider: { "@id": `${SITE}/#organization` },
      serviceType: "Pay-Per-Click Campaign Management",
      description: "Expert Google Ads and social media advertising management for maximum ROI."
    }
  ],

  "/mobile-apps": [
    orgSchema,
    {
      "@type": "Service",
      name: "Mobile App Development",
      provider: { "@id": `${SITE}/#organization` },
      serviceType: "Mobile Application Development",
      description: "Custom mobile app design and development for iOS and Android."
    }
  ],

  "/personal-portfolio": [
    orgSchema,
    {
      "@type": "Product",
      name: "Personal Portfolio Website",
      brand: { "@id": `${SITE}/#organization` },
      description: "Professional portfolio websites for creatives, freelancers, and professionals."
    }
  ],

  "/digital-marketing-platform": [
    orgSchema,
    {
      "@type": "SoftwareApplication",
      name: "Digital Marketing Platform",
      applicationCategory: "BusinessApplication",
      operatingSystem: "Web",
      description: "All-in-one digital marketing platform for managing SEO, social media, lead generation, and client reporting."
    }
  ],

  // ── Job Dispatch Pro ──
  "/jobstream": [
    {
      "@type": "Organization",
      "@id": `${JDP}/#organization`,
      name: "Job Dispatch Pro",
      url: JDP,
      logo: `${JDP}/logo.png`,
      parentOrganization: { "@id": `${SITE}/#organization` }
    },
    {
      "@type": "SoftwareApplication",
      "@id": `${JDP}/#app`,
      name: "Job Dispatch Pro",
      applicationCategory: "BusinessApplication",
      applicationSubCategory: "Field Service Management",
      operatingSystem: "Web",
      description: "All-in-one field service management software for home service professionals. Schedule jobs, send quotes, invoice clients, and get paid — from one dashboard.",
      featureList: "Job Scheduling, Client Management, Invoicing, Online Payments, Quotes, Calendar, CSV Import, Automated Reminders, Booking Widget",
      screenshot: `${JDP}/screenshot.png`,
      offers: [
        { "@type": "Offer", name: "Starter", price: "49", priceCurrency: "USD", billingIncrement: "P1M", description: "1 user, unlimited clients, jobs, calendar, basic invoicing" },
        { "@type": "Offer", name: "Pro", price: "99", priceCurrency: "USD", billingIncrement: "P1M", description: "Up to 10 users, quotes, online payments, client portal" },
        { "@type": "Offer", name: "Premium", price: "149", priceCurrency: "USD", billingIncrement: "P1M", description: "Unlimited users, reports, route optimization, GPS, API access" }
      ],
      aggregateRating: {
        "@type": "AggregateRating",
        ratingValue: "4.8",
        ratingCount: "342",
        bestRating: "5"
      }
    },
    {
      "@type": "FAQPage",
      mainEntity: [
        { "@type": "Question", name: "What is Job Dispatch Pro?", acceptedAnswer: { "@type": "Answer", text: "Job Dispatch Pro is a field service management platform built for home service businesses. It handles scheduling, client management, invoicing, and online payments in one dashboard." } },
        { "@type": "Question", name: "How much does Job Dispatch Pro cost?", acceptedAnswer: { "@type": "Answer", text: "Plans start at $49/month for Starter, $99/month for Pro, and $149/month for Premium with unlimited users." } },
        { "@type": "Question", name: "Can I try Job Dispatch Pro before signing up?", acceptedAnswer: { "@type": "Answer", text: "Yes! Click 'Try It Now' on our homepage to launch an interactive demo with sample data — no signup required." } },
        { "@type": "Question", name: "What payment methods are supported?", acceptedAnswer: { "@type": "Answer", text: "Job Dispatch Pro supports Stripe (credit/debit cards), PayPal, Venmo, Zelle, ACH bank transfers, and checks." } },
        { "@type": "Question", name: "Can I import my existing clients?", acceptedAnswer: { "@type": "Answer", text: "Yes, you can import clients via CSV upload. The system auto-creates client records and properties from your data." } },
      ]
    },
    {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: JDP },
        { "@type": "ListItem", position: 2, name: "Pricing", item: `${JDP}/#pricing` },
        { "@type": "ListItem", position: 3, name: "Features", item: `${JDP}/#features` },
      ]
    }
  ],
};

// ── Sitewide breadcrumb generator ──
function generateBreadcrumb(path) {
  if (path === "/" || PAGE_SCHEMAS[path]?.some(s => s["@type"] === "BreadcrumbList")) return null;
  const segments = path.split("/").filter(Boolean);
  const items = [{ "@type": "ListItem", position: 1, name: "Home", item: SITE }];
  let current = "";
  segments.forEach((seg, i) => {
    current += `/${seg}`;
    items.push({
      "@type": "ListItem",
      position: i + 2,
      name: seg.replace(/-/g, " ").replace(/\b\w/g, c => c.toUpperCase()),
      item: `${SITE}${current}`
    });
  });
  return { "@type": "BreadcrumbList", itemListElement: items };
}

export default function SchemaStack() {
  const { pathname } = useLocation();

  useEffect(() => {
    // Remove previous schema tags
    document.querySelectorAll('script[data-schema-stack]').forEach(el => el.remove());

    const schemas = PAGE_SCHEMAS[pathname] || [orgSchema];
    const breadcrumb = generateBreadcrumb(pathname);
    if (breadcrumb) schemas.push(breadcrumb);

    // Inject each schema as its own script tag (stacking)
    schemas.forEach((schema, i) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-schema-stack', `${pathname}-${i}`);
      script.textContent = JSON.stringify({ "@context": "https://schema.org", ...schema });
      document.head.appendChild(script);
    });

    return () => {
      document.querySelectorAll('script[data-schema-stack]').forEach(el => el.remove());
    };
  }, [pathname]);

  return null;
}
